<template>
  <div class="px-4 sm:px-6 lg:px-8 bg-gray-800 pt-6">
    <div class="sm:flex sm:items-center mb-4">
      <div class="sm:flex-auto text-left">
        <h1 class="text-xl font-semibold text-white">Zarządzanie Influencerami ({{ influencers.length }})</h1>
        <p class="mt-2 text-sm text-gray-400">Dodawaj i zarządzaj influencerami, którzy będą dostępni w dropdownie przy opiniach.</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
        <button @click="showAddForm = true" type="button" class="inline-flex items-center justify-center rounded-full border border-transparent bg-indigo-700 px-6 py-4 text-sm font-bold text-white shadow-sm hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 sm:w-auto">+ dodaj influencera</button>
      </div>
    </div>

    <!-- Modal for adding/editing influencer -->
    <div
      v-if="showAddForm || showEditForm"
      @click.self="cancelForm"
      class="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="mx-auto bg-gray-700 text-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
        <h2 id="modal-title" class="text-2xl font-bold mb-6">{{ showEditForm ? 'Edytuj influencera' : 'Dodaj nowego influencera' }}</h2>
        <form @submit.prevent="showEditForm ? updateInfluencer() : addInfluencer()" class="space-y-4">
          <div class="flex flex-wrap gap-4">
            <div class="flex-1 min-w-0">
              <label for="name" class="block text-sm font-medium text-white">Nazwa *</label>
              <input
                type="text"
                id="name"
                v-model="formData.name"
                placeholder="Nazwa influencera"
                class="mt-1 block w-full px-3 py-2 border text-black border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
            <div class="flex-1 min-w-0">
              <label for="url" class="block text-sm font-medium text-white">URL</label>
              <input
                type="url"
                id="url"
                v-model="formData.url"
                placeholder="https://instagram.com/username"
                class="mt-1 block w-full px-3 py-2 border text-black border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div class="flex flex-wrap gap-4">
            <div class="flex-1 min-w-0">
              <label for="platform" class="block text-sm font-medium text-white">Platforma</label>
              <select id="platform" v-model="formData.platform" class="mt-1 block w-full px-3 py-2 border text-white border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                <option value="">Wybierz platformę</option>
                <option value="Instagram">Instagram</option>
                <option value="YouTube">YouTube</option>
                <option value="TikTok">TikTok</option>
                <option value="Facebook">Facebook</option>
                <option value="Blog">Blog</option>
                <option value="Inne">Inne</option>
              </select>
            </div>
             <div class="flex-1 min-w-0">
              <label for="kolejnosc" class="block text-sm font-medium text-white">Kolejność</label>
              <input
                type="number"
                id="kolejnosc"
                v-model.number="formData.kolejnosc"
                placeholder="0"
                class="mt-1 block w-full px-3 py-2 border text-black border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div class="flex-1 min-w-0">
              <label for="avatar" class="block text-sm font-medium text-white">Avatar URL</label>
              <input
                type="url"
                id="avatar"
                v-model="formData.avatar"
                placeholder="https://example.com/avatar.jpg"
                class="mt-1 block w-full px-3 py-2 border text-black border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div class="w-full">
            <label for="description" class="block text-sm font-medium text-white">Opis</label>
            <textarea
              id="description"
              v-model="formData.description"
              rows="3"
              placeholder="Krótki opis influencera..."
              class="mt-1 block w-full px-3 py-2 border text-black border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            ></textarea>
          </div>
          <div class="flex justify-end space-x-3">
            <button
              @click="cancelForm"
              type="button"
              class="px-4 py-2 bg-gray-500 text-white font-semibold rounded-md shadow-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Anuluj
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-500 text-white font-semibold rounded-md shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              :disabled="isAdding || isUpdating"
            >
              {{ showEditForm ? (isUpdating ? 'Aktualizowanie...' : 'Aktualizuj') : (isAdding ? 'Dodawanie...' : 'Dodaj') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Lista influencerów -->
    <div class="mt-8 flex flex-col">
      <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-gray-700">
              <thead class="bg-gray-700">
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-200 sm:pl-6"><a href="#" class="group inline-flex" @click.prevent="sortBy('id')">ID<span class="ml-2 flex-none rounded" :class="{'invisible': !sortState.id.active}"><ChevronUpIcon class="h-5 w-5" v-if="!sortState.id.ascending" /><ChevronDownIcon class="h-5 w-5" v-if="sortState.id.ascending" /></span></a></th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"><a href="#" class="group inline-flex" @click.prevent="sortBy('name')">Nazwa<span class="ml-2 flex-none rounded" :class="{'invisible': !sortState.name.active}"><ChevronUpIcon class="h-5 w-5" v-if="!sortState.name.ascending" /><ChevronDownIcon class="h-5 w-5" v-if="sortState.name.ascending" /></span></a></th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"><a href="#" class="group inline-flex" @click.prevent="sortBy('platform')">Platforma<span class="ml-2 flex-none rounded" :class="{'invisible': !sortState.platform.active}"><ChevronUpIcon class="h-5 w-5" v-if="!sortState.platform.ascending" /><ChevronDownIcon class="h-5 w-5" v-if="sortState.platform.ascending" /></span></a></th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-200">URL</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"><a href="#" class="group inline-flex" @click.prevent="sortBy('kolejnosc')">Kolejność<span class="ml-2 flex-none rounded" :class="{'invisible': !sortState.kolejnosc.active}"><ChevronUpIcon class="h-5 w-5" v-if="!sortState.kolejnosc.ascending" /><ChevronDownIcon class="h-5 w-5" v-if="sortState.kolejnosc.ascending" /></span></a></th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-200">Opis</th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span class="sr-only">Akcje</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-700 bg-gray-800">
                <tr v-for="influencer in influencers" :key="influencer.id" class="hover:bg-gray-700 transition-colors">
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-white sm:pl-6">{{ influencer.id }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-400">
                    <div class="flex items-center">
                      <img v-if="influencer.avatar || influencer.avatar_url" :src="influencer.avatar || influencer.avatar_url" :alt="influencer.name" class="w-8 h-8 rounded-full mr-3 object-cover" />
                      {{ influencer.name }}
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-400">
                    <span v-if="influencer.platform" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {{ influencer.platform }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-400">
                    <a v-if="influencer.url" :href="influencer.url" target="_blank" class="text-indigo-400 hover:text-indigo-300">
                      {{ influencer.url }}
                    </a>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-400">{{ influencer.kolejnosc || 0 }}</td>
                  <td class="px-3 py-4 text-sm text-gray-400">
                    <div class="max-w-xs truncate" :title="influencer.description">
                      {{ influencer.description }}
                    </div>
                  </td>
                  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <div class="flex justify-end space-x-2">
                      <input
                        class="hidden"
                        type="file"
                        accept="image/*"
                        :id="`avatar-input-${influencer.id}`"
                        @change="onAvatarPick($event, influencer)"
                      />
                      <button
                        @click="triggerAvatarInput(influencer.id)"
                        class="text-yellow-400 hover:text-yellow-300 bg-gray-800 hover:bg-gray-600 px-3 py-1 rounded-full"
                      >Avatar</button>
                      <button
                        @click="editInfluencer(influencer)"
                        class="text-blue-400 hover:text-blue-300 bg-gray-800 hover:bg-gray-600 px-3 py-1 rounded-full"
                      >
                        Edytuj
                      </button>
                      <button
                        @click="deleteInfluencer(influencer.id)"
                        class="text-red-400 hover:text-red-300  bg-gray-800 hover:bg-gray-600 px-3 py-1 rounded-full"
                        :disabled="isDeleting === influencer.id"
                      >
                        {{ isDeleting === influencer.id ? 'Usuwanie...' : 'Usuń' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, onUnmounted } from 'vue';
import { apiUrl } from '../composables/apiBase';
import { apiFetch } from '../composables/apiClient';
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";

const influencers = ref([]);
const showAddForm = ref(false);
const showEditForm = ref(false);
const isAdding = ref(false);
const isUpdating = ref(false);
const isDeleting = ref(null);

const sortState = reactive({
  id: { ascending: true, active: true },
  name: { ascending: true, active: false },
  platform: { ascending: true, active: false },
  kolejnosc: { ascending: true, active: false },
});

const formData = ref({
  id: null,
  name: '',
  url: '',
  platform: '',
  avatar: '',
  description: '',
  kolejnosc: 0
});

const sortBy = (key) => {
  Object.keys(sortState).forEach((k) => (sortState[k].active = false));

  sortState[key].active = true;
  sortState[key].ascending = !sortState[key].ascending;

  influencers.value.sort((a, b) => {
    let valA = a[key] === null || a[key] === undefined ? '' : a[key];
    let valB = b[key] === null || b[key] === undefined ? '' : b[key];

    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortState[key].ascending ? valA - valB : valB - valA;
    }

    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortState[key].ascending
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    
    // Fallback for mixed types or other types
    if (valA < valB) return sortState[key].ascending ? -1 : 1;
    if (valA > valB) return sortState[key].ascending ? 1 : -1;
    return 0;
  });
};

// Pobieranie influencerów
const fetchInfluencers = async () => {
  try {
    const response = await apiFetch('/api/influencers');
    if (response.ok) {
      const data = await response.json();
      // Normalize avatar URLs to absolute
      influencers.value = (data.data || []).map((inf) => {
        const clone = { ...inf };
        const makeAbsolute = (u) => {
          if (!u) return u;
          if (/^https?:\/\//i.test(u)) return u;
          return apiUrl(u.startsWith('/') ? u : `/${u}`);
        };
        if (clone.avatar) clone.avatar = makeAbsolute(clone.avatar);
        if (clone.avatar_url) clone.avatar_url = makeAbsolute(clone.avatar_url);
        return clone;
      });
      sortBy('id');
    } else {
      console.error('Błąd podczas pobierania influencerów');
    }
  } catch (error) {
    console.error('Błąd:', error);
  }
};

// Dodawanie influencera
const addInfluencer = async () => {
  isAdding.value = true;
  try {
    const payload = { ...formData.value };
    delete payload.id; // Usuwamy ID przed wysłaniem

    const response = await apiFetch(
      "/api/influencers",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
      const data = await response.json();
      influencers.value.push(data.data);
      cancelForm();
      console.log("Influencer dodany pomyślnie!");
    } else {
      const errorText = await response.text();
      console.error("Błąd podczas dodawania influencera:", errorText);
      alert(`Nie udało się dodać influencera: ${errorText}`);
    }
  } catch (error) {
    console.error("Błąd:", error);
  } finally {
    isAdding.value = false;
  }
};

// Aktualizacja influencera
const updateInfluencer = async () => {
  isUpdating.value = true;
  try {
    const payload = { ...formData.value };
    delete payload.id; // Usuwamy ID przed wysłaniem

    const response = await apiFetch(`/api/influencers/${formData.value.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (response.ok) {
      const data = await response.json();
      const index = influencers.value.findIndex(
        (inf) => inf.id === formData.value.id
      );
      if (index !== -1) {
        influencers.value[index] = data.data;
      }
      cancelForm();
      console.log("Influencer zaktualizowany pomyślnie!");
    } else {
      const errorText = await response.text();
      console.error("Błąd podczas aktualizacji influencera:", errorText);
      alert(`Nie udało się zaktualizować influencera: ${errorText}`);
    }
  } catch (error) {
    console.error("Błąd:", error);
  } finally {
    isUpdating.value = false;
  }
};

// Usuwanie influencera
const deleteInfluencer = async (id) => {
  if (!confirm('Czy na pewno chcesz usunąć tego influencera?')) return;
  
  isDeleting.value = id;
  try {
    const response = await apiFetch(`/api/influencers/${id}`, {
      method: 'DELETE',
    });
    
    if (response.ok) {
      influencers.value = influencers.value.filter(inf => inf.id !== id);
      console.log('Influencer usunięty pomyślnie!');
    } else {
      console.error('Błąd podczas usuwania influencera');
    }
  } catch (error) {
    console.error('Błąd:', error);
  } finally {
    isDeleting.value = null;
  }
};

// Anulowanie dodawania/edycji
const cancelForm = () => {
  showAddForm.value = false;
  showEditForm.value = false;
  formData.value = {
    id: null,
    name: '',
    url: '',
    platform: '',
    avatar: '',
    description: '',
    kolejnosc: 0
  };
};

// Funkcja do edycji influencera
const editInfluencer = (influencer) => {
  showAddForm.value = false;
  showEditForm.value = true;
  formData.value = { ...influencer };
};

const handleEscapeKey = (event) => {
  if (event.key === 'Escape') {
    cancelForm();
  }
};

onMounted(() => {
  fetchInfluencers();
  window.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey);
});

// Avatar upload helpers
const triggerAvatarInput = (id) => {
  const el = document.getElementById(`avatar-input-${id}`);
  if (el) el.click();
};

const onAvatarPick = async (event, influencer) => {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  try {
    const form = new FormData();
    form.append('avatar', file);
    const res = await apiFetch(`/api/influencers/${influencer.id}/avatar`, {
      method: 'POST',
      body: form,
      // credentials: 'include' // odkomentuj jeśli wymagane ciasteczka auth
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Upload failed');
    }
    const data = await res.json();
    // Backend powinien zwrócić { url }
    if (data?.url) {
      const makeAbsolute = (u) => (/^https?:\/\//i.test(u) ? u : apiUrl(u.startsWith('/') ? u : `/${u}`));
      const absolute = makeAbsolute(data.url);
      influencer.avatar = absolute; // zgodnie z API (.avatar)
      influencer.avatar_url = absolute; // zachowaj też kompatybilność w UI
    }
  } catch (e) {
    console.error('Avatar upload error:', e);
    alert(`Błąd podczas uploadu avatara: ${e.message}`);
  } finally {
    // reset input value, by móc ponownie wybrać ten sam plik
    event.target.value = '';
  }
};
</script> 